<template>
  <div>
    <div class="mb-4 flex w-full flex-wrap items-center justify-between gap-2">
      <BaseSegmentedControl
        v-model="localFilters.status"
        :options="statusFilterOptions"
      />
      <BaseDateIntervalSelect
        :model-value="dateRange"
        @update:model-value="emit('update:dateRange', $event)"
      />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseInputSearch v-model="localFilters.search" />
      <BasePagination v-model="page" :page-count="pageCount" class="ml-auto" />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseCountSelect
        v-model="paginationCount"
        :total-count="data?.count || 0"
      />
    </div>
    <BaseTable
      key-field="id"
      :rows="rows"
      :columns="columns"
      :is-loading="isLoading"
    >
      <template #body-cell:enteredAt="{ value }">
        <p class="w-48 font-semibold text-black">
          {{ formatDatetimeToDatetimeFull(value) }}
        </p>
      </template>

      <template #body-cell:site="{ value }">
        <span v-if="value">{{ value }}</span>
        <span v-else class="text-gray-400">—</span>
      </template>

      <template #body-cell:enteredCode="{ value }">
        <span class="font-mono font-semibold">
          {{ value }}
        </span>
      </template>

      <template #body-cell:status="{ value }">
        <span
          :class="[
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
            getStatusClasses(value),
          ]"
        >
          {{ getStatusLabel(value) }}
        </span>
      </template>

      <template #body-cell:image="{ value }">
        <a
          v-if="value"
          :href="value"
          target="_blank"
          rel="noopener"
          title="Keypad entry image"
        >
          <img
            :src="value"
            alt="Keypad entry"
            class="h-12 w-auto rounded border border-gray-200 object-cover"
            loading="lazy"
          />
        </a>
        <span v-else class="text-gray-400">—</span>
      </template>
    </BaseTable>
  </div>
</template>

<script setup lang="ts">
import {
  type KeypadEvent,
  KeypadEventStatusOptions,
} from "@/services/keypadEvents/keypadEvents";
import { useCurrentSite } from "@/services/locations/useCurrentSite";
import { type ColumnDefinition } from "@/types/table";

type DateInterval = {
  start?: Date;
  end?: Date;
};

// The visible date range arrives as v-model:date-range so the caller owns it —
// the Entry Log page binds the shared model (useDatePickerContext) to keep one
// range across its ANPR and Keypad tabs. Don't mirror it into localFilters:
// that's spread into the query after it, so a local copy shadows the bound one
// and the picker silently stops driving the query.
const props = defineProps<{
  dateRange: DateInterval;
}>();

const emit = defineEmits<{
  (e: "update:dateRange", value: DateInterval): void;
}>();

interface LocalFilters {
  status: "valid" | "invalid" | "expired" | "cleared" | null;
  search: string;
}

const { data: sites } = useSites();
const { id: currentSiteId } = useCurrentSite();
const siteMap = computed(() => {
  const map = new Map<string, string>();
  sites.value?.forEach((site) => map.set(site.id, site.name));
  return map;
});

const columns = computed<ColumnDefinition<KeypadEvent>[]>(() => [
  {
    label: "Time",
    name: "enteredAt",
    getValue: (row) => row.enteredAt,
    autoWidth: true,
  },
  // Site (with keypad device) only when viewing all sites.
  ...(currentSiteId.value == null
    ? [
        {
          label: "Site",
          name: "site",
          getValue: (row: KeypadEvent) => {
            const siteName = siteMap.value.get(row.siteId);
            if (!siteName) return null;
            return row.deviceName
              ? `${siteName} (${row.deviceName})`
              : siteName;
          },
          autoWidth: true,
        },
      ]
    : []),
  {
    label: "Code",
    name: "enteredCode",
    getValue: (row) => row.enteredCode,
    autoWidth: true,
  },
  {
    label: "Status",
    name: "status",
    getValue: (row) => row.status,
    autoWidth: true,
  },
  {
    label: "Image",
    name: "image",
    getValue: (row) => row.imageUrl ?? "",
    autoWidth: true,
  },
]);

const localFilters = reactive<LocalFilters>({
  status: null,
  search: "",
});

const paginationCount = ref(100);

const { pagination, page, pageCount, resetPagination } = usePagination({
  limit: paginationCount,
  count: computed(() => data.value?.count || 0),
});

watch(
  () => [props.dateRange, localFilters],
  () => resetPagination(),
  { deep: true, immediate: true },
);

const query = reactiveComputed(() => ({
  ...localFilters,
  enteredAtFrom: props.dateRange.start,
  enteredAtTo: props.dateRange.end,
  ...pagination.value,
}));

const { data, isLoading } = useKeypadEvents(query);

const rows = computed(() => data.value?.items || []);

const statusFilterOptions = [
  { label: "All", value: null },
  ...KeypadEventStatusOptions,
];

const getStatusClasses = (status: string) => {
  switch (status) {
    case "valid":
      return "bg-green-100 text-green-800";
    case "invalid":
      return "bg-red-100 text-red-800";
    case "expired":
      return "bg-red-100 text-red-800";
    case "cleared":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusLabel = (status: string) => {
  const option = KeypadEventStatusOptions.find((opt) => opt.value === status);
  return option?.label || status;
};
</script>
